/**
 * `?next=` - where to land after signing in (M2.8).
 *
 * The value arrives from a URL, which means it arrives from anyone who can get an
 * operator to click a link. An open redirect on the sign-in page of the console that
 * administers every tenant is the single most valuable one in the platform: the operator
 * signs in on the real site, and the real site then sends them somewhere else entirely.
 *
 * The rule is narrow and has no exceptions: the target must be a path on this app.
 * Anything else - an absolute URL, a protocol-relative `//evil.example`, a backslash that
 * some parsers read as a slash, a control character - resolves to the fallback.
 *
 * This is the patient app's `safeNextPath` with a different default, kept as a copy
 * rather than shared because it is eight lines and the alternative is a package boundary
 * around a security check that must be readable where it is used.
 */

/** Where an operator goes when there is no usable `next`. */
export const DEFAULT_NEXT_PATH = '/';

export function safeNextPath(raw: unknown, fallback: string = DEFAULT_NEXT_PATH): string {
  if (typeof raw !== 'string') return fallback;

  const value = raw.trim();
  if (value === '') return fallback;

  // Must be a rooted path, and only one slash: `//host` is protocol-relative.
  if (!value.startsWith('/') || value.startsWith('//')) return fallback;
  // `\` is a path separator to some URL parsers, so `/\evil.example` is an escape.
  if (value.includes('\\')) return fallback;
  // A control character anywhere is a smuggling attempt, never a real route.
  // eslint-disable-next-line no-control-regex
  if (/[ -]/.test(value)) return fallback;
  // Sending someone back to the screen they just left is a loop, not a destination.
  if (value === '/login' || value.startsWith('/login?')) return fallback;

  return value;
}

/** `/login?next=<here>` for the route the operator was trying to reach. */
export function loginPathFor(pathname: string, search: string = ''): string {
  const target = safeNextPath(`${pathname}${search}`, DEFAULT_NEXT_PATH);
  return `/login?next=${encodeURIComponent(target)}`;
}

/**
 * Whether the browser may be sent to `raw` after starting impersonation.
 *
 * `POST /platform/impersonation` answers with a `redirectUrl` into the **tenant's admin
 * host**, which is a different origin from this console. That value comes from the API
 * and is therefore trusted far more than a query parameter - but it is still a URL this
 * app is about to navigate to, so it is checked rather than assumed:
 *
 * - the protocol must match this page's (no downgrade, no `javascript:`);
 * - the host must be this origin, or a sibling under the same parent domain - which is
 *   exactly what a tenant subdomain of the platform domain is.
 *
 * Anything else is refused and the caller falls back to refreshing the session, which is
 * a worse experience and a correct one. A platform domain configured per environment
 * would let this be an exact match instead; see `Docs/SUPER_ADMIN_UI.md` open items.
 */
export function isTrustedRedirect(
  raw: string,
  current: { protocol: string; host: string },
): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }

  if (url.protocol !== current.protocol) return false;
  if (url.host === current.host) return true;

  // `admin.heyallergy.test` and `bay-allergy.heyallergy.test` share a parent; a bare
  // hostname with no parent (`localhost`) never matches anything but itself.
  const parent = (host: string): string | null => {
    const hostname = host.split(':')[0] ?? host;
    const labels = hostname.split('.');
    return labels.length < 3 ? null : labels.slice(1).join('.');
  };
  const here = parent(current.host);
  return here !== null && here === parent(url.host);
}
